import { DateTime } from "luxon";

import { ajaxController } from "./controller/AjaxController";
import { clientMutationOptions } from "./controller/clientMutation";
import { PagedResult } from "../DTOs/Common/PagedResult";
import {
  ComponentActionResultDTO,
  ComponentLifeDTO,
  ComponentLifeEstimateDTO,
  ConsumableItemDTO,
  ConsumablePostResultDTO,
  ConsumableReorderDTO,
  ConsumableStockDTO,
  ConsumableStockLevelDTO,
  ConsumableStocktakeSheetLineDTO,
  ConsumableStoreDTO,
  ConsumableTransactionDTO,
  ConsumableTypeDTO,
  DrillComponentDTO,
  FitComponentRequest,
  PendingConsumableStocktakeDTO,
  PostConsumableTransactionRequest,
  RemoveComponentRequest,
  RigShiftDTO,
  SaveConsumableItemRequest,
  SaveConsumableStoreRequest,
  StocktakeSubmittedDTO,
  SubmitConsumableStocktakeRequest
} from "../DTOs/DrillBlast/ConsumablesDTO";
import { TripleShift } from "../types/Shift";

class ConsumablesAjax {
  private _ajaxController = ajaxController;
  private _endpoint = "/Consumables";

  public getStores() {
    return this._ajaxController.get<PagedResult<ConsumableStoreDTO>>(`${this._endpoint}/stores`, {});
  }

  public addStore(request: SaveConsumableStoreRequest) {
    return this._ajaxController.post<SaveConsumableStoreRequest, ConsumableStoreDTO>(`${this._endpoint}/stores`, request);
  }

  public updateStore(storeId: number, request: SaveConsumableStoreRequest) {
    return this._ajaxController.put<SaveConsumableStoreRequest, ConsumableStoreDTO>(`${this._endpoint}/stores/${storeId}`, request);
  }

  public getTypes() {
    return this._ajaxController.get<PagedResult<ConsumableTypeDTO>>(`${this._endpoint}/types`, {});
  }

  public getItems(includeInactive = false) {
    return this._ajaxController.get<PagedResult<ConsumableItemDTO>>(`${this._endpoint}/items`, { includeInactive });
  }

  public addItem(request: SaveConsumableItemRequest) {
    return this._ajaxController.post<SaveConsumableItemRequest, ConsumableItemDTO>(`${this._endpoint}/items`, request);
  }

  public updateItem(itemId: number, request: SaveConsumableItemRequest) {
    return this._ajaxController.put<SaveConsumableItemRequest, ConsumableItemDTO>(`${this._endpoint}/items/${itemId}`, request);
  }

  public getStockLevels() {
    return this._ajaxController.get<PagedResult<ConsumableStockLevelDTO>>(`${this._endpoint}/stock-levels`, {});
  }

  public saveStockLevel(level: ConsumableStockLevelDTO) {
    return this._ajaxController.put<ConsumableStockLevelDTO, ConsumableStockLevelDTO>(`${this._endpoint}/stock-levels`, level);
  }

  public getStock() {
    return this._ajaxController.get<PagedResult<ConsumableStockDTO>>(`${this._endpoint}/stock`, {});
  }

  public getReorderList() {
    return this._ajaxController.get<PagedResult<ConsumableReorderDTO>>(`${this._endpoint}/reorder`, {});
  }

  public getTransactions(from: DateTime, to: DateTime, storeId?: number | null) {
    return this._ajaxController.get<PagedResult<ConsumableTransactionDTO>>(`${this._endpoint}/transactions`, {
      from: from.toISODate(),
      to: to.toISODate(),
      ...(storeId ? { storeId } : {}),
    });
  }

  public post(request: PostConsumableTransactionRequest) {
    return this._ajaxController.post<PostConsumableTransactionRequest, ConsumablePostResultDTO>(
      `${this._endpoint}/transactions`, request, undefined, undefined, clientMutationOptions(request.clientMutationId ?? undefined));
  }

  public postBulkIssue(request: PostConsumableTransactionRequest) {
    return this._ajaxController.post<PostConsumableTransactionRequest, ConsumablePostResultDTO>(
      `${this._endpoint}/bulk-issues`, request, undefined, undefined, clientMutationOptions(request.clientMutationId ?? undefined));
  }

  public getParked() {
    return this._ajaxController.get<PagedResult<ConsumableTransactionDTO>>(`${this._endpoint}/parked`, {});
  }

  public postParked(transactionId: number, note: string | null, acknowledge: boolean) {
    return this._ajaxController.post<{ note: string | null; acknowledge: boolean }, ConsumablePostResultDTO>(
      `${this._endpoint}/parked/${transactionId}/post`, { note, acknowledge });
  }

  public discardParked(transactionId: number, note: string) {
    return this._ajaxController.post<{ note: string }, ConsumableTransactionDTO>(`${this._endpoint}/parked/${transactionId}/discard`, { note });
  }

  public reverse(transactionId: number, reason: string) {
    return this._ajaxController.post<{ reason: string }, ConsumableTransactionDTO>(`${this._endpoint}/transactions/${transactionId}/reverse`, { reason });
  }

  public getRigShifts(date: DateTime, shift: TripleShift) {
    return this._ajaxController.get<PagedResult<RigShiftDTO>>(`${this._endpoint}/rig-shifts/${date.toISODate()}/${shift[0]}`, {});
  }

  public getComponents(includeRetired = false) {
    return this._ajaxController.get<PagedResult<DrillComponentDTO>>(`${this._endpoint}/components`, { includeRetired });
  }

  public getFittable(plantId: number) {
    return this._ajaxController.get<PagedResult<DrillComponentDTO>>(`${this._endpoint}/components/fittable`, { plantId });
  }

  public getEstimate(componentId: number, at: DateTime) {
    return this._ajaxController.get<ComponentLifeEstimateDTO>(`${this._endpoint}/components/${componentId}/estimate`, { at: at.toUTC().toISO() });
  }

  public getLifeAnalysis() {
    return this._ajaxController.get<PagedResult<ComponentLifeDTO>>(`${this._endpoint}/components/life`, {});
  }

  public fit(request: FitComponentRequest) {
    return this._ajaxController.post<FitComponentRequest, ComponentActionResultDTO>(
      `${this._endpoint}/components/fit`, request, undefined, undefined, clientMutationOptions(request.clientMutationId ?? undefined));
  }

  public remove(request: RemoveComponentRequest) {
    return this._ajaxController.post<RemoveComponentRequest, ComponentActionResultDTO>(
      `${this._endpoint}/components/remove`, request, undefined, undefined, clientMutationOptions(request.clientMutationId ?? undefined));
  }

  public returnFromRegrind(componentId: number, storeId: number, occurredAt: DateTime, rowVersion: string | null) {
    return this._ajaxController.post<object, ComponentActionResultDTO>(
      `${this._endpoint}/components/return-from-regrind`, { componentId, storeId, occurredAt, rowVersion });
  }

  public getStocktakeSheet(storeId: number) {
    return this._ajaxController.get<PagedResult<ConsumableStocktakeSheetLineDTO>>(`${this._endpoint}/stores/${storeId}/stocktake-sheet`, {});
  }

  public submitStocktake(request: SubmitConsumableStocktakeRequest) {
    return this._ajaxController.post<SubmitConsumableStocktakeRequest, StocktakeSubmittedDTO>(`${this._endpoint}/stocktakes`, request);
  }

  public getPendingStocktakes() {
    return this._ajaxController.get<PagedResult<PendingConsumableStocktakeDTO>>(`${this._endpoint}/stocktakes/pending`, {});
  }

  public approveStocktake(stocktakeId: number, comment: string, rowVersion?: string | null) {
    return this._ajaxController.post<{ comment: string; rowVersion?: string | null }, boolean>(`${this._endpoint}/stocktakes/${stocktakeId}/approve`, { comment, rowVersion });
  }

  public cancelStocktake(stocktakeId: number, comment: string, rowVersion?: string | null) {
    return this._ajaxController.post<{ comment: string; rowVersion?: string | null }, boolean>(`${this._endpoint}/stocktakes/${stocktakeId}/cancel`, { comment, rowVersion });
  }
}

export const consumablesAjax = new ConsumablesAjax();
